import { getStandardPodcastCategories } from "../constants/standardPodcasts";
import { GlobalCarousel, HomeRow, Station } from "../types";

import { enrichStationData } from "./radioService";
import { supabase } from "./supabase";
import { getUserHomePreferences } from "./userLibraryService";

interface GlobalCarouselRow {
  id: string;
  name: string;
  title: string;
  subtitle: string;
  type: HomeRow["type"];
  station_ids: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter_rules: any;
  countries: string[];
  position: string;
  section: "radio" | "podcast";
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Fetches global carousels that should be displayed for a specific country.
 * Filters by enabled status, country match, section (radio/podcast), and user preferences.
 */
export const fetchGlobalCarouselsForCountry = async (
  country: string,
  section: "radio" | "podcast" = "radio",
  userId?: string,
): Promise<GlobalCarousel[]> => {
  try {
    const { data, error } = await supabase
      .from("global_carousels")
      .select("*")
      .eq("enabled", true)
      // .eq('section', section) // Temporarily remove DB filter to debug section mismatch
      .order("created_at", { ascending: false });

    if (error) throw error;

    if (!data) return [];

    // Fetch user preferences if userId is provided
    const hiddenSectionIds = new Set<string>();
    if (userId) {
      const prefs = await getUserHomePreferences(userId);
      prefs.forEach((p) => {
        if (!p.isVisible) hiddenSectionIds.add(p.sectionId);
      });
    }

    // Normalize country: treat "Worldwide" as "All"
    const normalizedCountry = country === "Worldwide" ? "All" : country;

    // Filter carousels that apply to this country AND section
    const filtered = data.filter((carousel: GlobalCarouselRow) => {
      // Check if hidden by user
      if (hiddenSectionIds.has(carousel.id)) {
        return false;
      }

      // Check section match
      const carouselSection = carousel.section || "radio"; // Default to radio if null
      if (carouselSection !== section) {
        return false;
      }

      const countries = carousel.countries || [];

      // STRICT COUNTRY FILTERING (USP Feature):
      // - If user selected a specific country (e.g., "Sweden"), show ONLY that country's carousels
      // - If user selected "All/Worldwide", show ONLY global carousels marked with "All"
      // This creates a clean, focused experience per country
      const isMatch =
        normalizedCountry === "All"
          ? countries.includes("All") // Worldwide mode: show only global carousels
          : countries.includes(normalizedCountry); // Country mode: show only that country's carousels

      return isMatch;
    });

    // Map database columns to TypeScript interface
    return filtered.map((row: GlobalCarouselRow) => ({
      id: row.id,
      name: row.name,
      title: row.title,
      subtitle: row.subtitle,
      type: row.type,
      stationIds: row.station_ids || [], // Legacy
      filter_rules: row.filter_rules, // NEW (snake_case to match interface)
      countries: row.countries || [],
      position: row.position || "bottom",
      section: row.section || "radio",
      enabled: row.enabled,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    }));
  } catch (e) {
    console.error("[fetchGlobalCarouselsForCountry] Error:", e);
    return [];
  }
};

/**
 * Fetches all global carousels for a country, including user's visibility preference.
 * Used for the "Customize Home" UI.
 */
export const getPersonalizedCarouselSettings = async (
  country: string,
  userId: string,
): Promise<{ carousel: GlobalCarousel; isVisible: boolean }[]> => {
  try {
    // 1. Fetch all enabled carousels for this country (unfiltered)
    const radioCountry = await fetchGlobalCarouselsForCountry(country, "radio");
    const radioAll = await fetchGlobalCarouselsForCountry("All", "radio");
    const podCountry = await fetchGlobalCarouselsForCountry(country, "podcast");
    const podAll = await fetchGlobalCarouselsForCountry("All", "podcast");

    let allCarousels = [...radioCountry, ...radioAll, ...podCountry, ...podAll];

    // 2. Add Standard Podcast Categories (Mocked as GlobalCarousel)
    // These are hardcoded in podcastService but we want them toggleable
    const standardPods = getStandardPodcastCategories(country);
    const standardPodCarousels: GlobalCarousel[] = standardPods.map((cat) => ({
      id: cat.id,
      name: cat.title,
      title: cat.title,
      subtitle: cat.sub,
      section: "podcast",
      position: "standard",
      type: cat.type || "genre",
      countries: [country],
      stationIds: [],
      enabled: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    allCarousels = [...allCarousels, ...standardPodCarousels];

    // Combine and deduplicate
    const unique = new Map<string, GlobalCarousel>();
    allCarousels.forEach((c) => unique.set(c.id, c));
    allCarousels = Array.from(unique.values());

    // 3. Fetch user's hidden preferences
    const hiddenIds = await getUserHomePreferences(userId); // Returns list of hidden items

    // 4. Map to Settings format
    return allCarousels.map((carousel) => ({
      carousel,
      isVisible: !hiddenIds.some(
        (h) => h.sectionId === carousel.id && !h.isVisible,
      ),
    }));
  } catch (e) {
    console.error("[getPersonalizedCarouselSettings] Error:", e);
    return [];
  }
};

/**
 * Fetches the actual station data for a global carousel using filter rules.
 */
export const fetchStationsForCarousel = async (
  carousel: GlobalCarousel,
): Promise<Station[]> => {
  try {
    // Import dynamically to avoid circular dependency
    const { executeCarouselFilter } = await import("./carouselQueryService");

    // Old behavior: If carousel has stationIds (legacy format), use them
    if (carousel.stationIds && carousel.stationIds.length > 0) {
      console.warn(
        `[fetchStationsForCarousel] Legacy carousel "${carousel.name}" using stationIds array`,
      );
      return await fetchStationsByIdsLegacy(carousel.stationIds);
    }

    // New behavior: Use filter_rules from database
    if (carousel.filter_rules) {
      return await executeCarouselFilter(carousel.filter_rules);
    }

    console.error(
      `[fetchStationsForCarousel] No filter_rules found for carousel: ${carousel.name}`,
    );
    return [];
  } catch (e) {
    console.error("[fetchStationsForCarousel] Error:", e);
    return [];
  }
};

/**
 * Legacy function to fetch stations by IDs (for backwards compatibility).
 */

// Countries to exclude from carousel results
const BLOCKED_COUNTRIES = new Set([
  "russia",
  "russian federation",
  "china",
  "iran",
  "north korea",
  "syria",
  "belarus",
  "cuba",
  "venezuela",
  "nicaragua",
  "eritrea",
  "turkmenistan",
  "afghanistan",
  "iraq",
  "yemen",
  "libya",
  "sudan",
  "somalia",
  "egypt",
  "saudi arabia",
  "kuwait",
  "united arab emirates",
  "qatar",
  "bahrain",
  "oman",
  "jordan",
  "lebanon",
  "algeria",
  "morocco",
  "tunisia",
  "palestine",
  "hungary",
  "israel",
  "ru",
  "cn",
  "ir",
  "kp",
  "sy",
  "by",
  "cu",
  "ve",
  "ni",
  "er",
  "tm",
  "af",
  "iq",
  "ye",
  "ly",
  "sd",
  "so",
  "eg",
  "sa",
  "kw",
  "ae",
  "qa",
  "bh",
  "om",
  "jo",
  "lb",
  "dz",
  "ma",
  "tn",
  "ps",
  "hu",
  "il",
]);

// Block stream URL patterns from sensitive countries
const BLOCKED_URL_PATTERNS = [
  ".ru/",
  ".ru:",
  "//ru.",
  ".cn/",
  ".cn:",
  "//cn.",
  ".ir/",
  ".by/",
  ".kp/",
  ".sy/",
  "europaplus",
  "europeplus",
  "europa-plus",
  "emg.fm",
];

// Block station name patterns
const BLOCKED_NAME_PATTERNS = [
  "europa plus",
  "europaplus",
  "europe plus",
  "europeplus",
  "radio rossii",
  "radio russia",
  "russian radio",
  "голос",
  "радио",
  "россия",
  "中国",
  "电台",
  // Religious content
  "christian",
  "gospel",
  "worship",
  "praise",
  "bible",
  "jesus",
  "christ",
  "catholic",
  "protestant",
  "evangelical",
  "baptist",
  "lutheran",
  "methodist",
  "pentecostal",
  "adventist",
  "mormon",
  "jehovah",
  "islamic",
  "muslim",
  "quran",
  "mosque",
  "allah",
  "muhammad",
  "hindu",
  "krishna",
  "temple",
  "mandir",
  "buddhist",
  "dharma",
  "sangha",
  "jewish",
  "torah",
  "synagogue",
  "kosher",
  "religious",
  "faith",
  "prayer",
  "sermon",
  "church radio",
  "god radio",
];

// Blocked tags
const BLOCKED_TAGS = new Set([
  "christian",
  "gospel",
  "religious",
  "religion",
  "worship",
  "praise",
  "catholic",
  "protestant",
  "evangelical",
  "baptist",
  "lutheran",
  "islamic",
  "muslim",
  "hindu",
  "buddhist",
  "jewish",
  "faith",
  "prayer",
  "church",
  "bible",
  "quran",
  "torah",
  "spiritual",
]);

const isBlockedCountry = (station: Station): boolean => {
  const country = (station.country || "").toLowerCase();
  const countryCode = (station.countryCode || "").toLowerCase();
  const name = (station.name || "").toLowerCase();
  const streamUrl = (station.streamUrl || "").toLowerCase();
  const tags = (station.tags || []).map((t) => t.toLowerCase());

  if (BLOCKED_COUNTRIES.has(country) || BLOCKED_COUNTRIES.has(countryCode))
    return true;
  if (BLOCKED_URL_PATTERNS.some((p) => streamUrl.includes(p))) return true;
  if (BLOCKED_NAME_PATTERNS.some((p) => name.includes(p))) return true;
  if (tags.some((tag) => BLOCKED_TAGS.has(tag))) return true;

  return false;
};

const fetchStationsByIdsLegacy = async (
  stationIds: string[],
): Promise<Station[]> => {
  try {
    const { data, error } = await supabase
      .from("stations")
      .select("*")
      .in("id", stationIds);

    if (error) throw error;
    if (!data) return [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stations = data.map((s: any) => ({
      id: s.id,
      name: s.name,
      country: s.country,
      countryCode: s.country_code,
      state: s.state,
      provider: s.provider,
      streamUrl: s.stream_url,
      website: s.website,
      logoUrl: s.logo_url,
      bitrate: s.bitrate,
      codec: s.codec,
      language: s.language,
      tags: s.tags || [],
      votes: s.votes || 0,
      clickTrend: s.click_trend || 0,
      onlineStatus: s.online_status,
    }));
    // Enrich with static station data and filter blocked countries
    return stations.map(enrichStationData).filter((s) => !isBlockedCountry(s));
  } catch (e) {
    console.error("[fetchStationsByIdsLegacy] Error:", e);
    return [];
  }
};

/**
 * Converts a GlobalCarousel to a HomeRow with actual station data.
 */
export const carouselToHomeRow = async (
  carousel: GlobalCarousel,
): Promise<HomeRow | null> => {
  const stations = await fetchStationsForCarousel(carousel);

  if (stations.length === 0) {
    console.warn(
      `[carouselToHomeRow] No stations found for carousel: ${carousel.name}`,
    );
    return null;
  }

  return {
    id: `global_${carousel.id}`,
    title: carousel.title,
    subtitle: carousel.subtitle,
    stations,
    type: carousel.type,
  };
};
